/**
 * Telas do cliente: consultas à API (/app/*), mutação de interesse e formatação.
 * Os contratos exportam o schema `Empresa` (sem tipo); o tipo sai do parse.
 */
import type { AppEdital, Empresa, EmpresaInput, EmpresaResumo, Etapa, INTERESSES, Notificacao, PORTES, UFS } from '@licita/contracts';
import { ETAPAS, PORTE_NOMES, UFS as UFS_LISTA } from '@licita/contracts';
import { queryOptions } from '@tanstack/react-query';
import { api } from './api';
import { queryClient } from './queries';

export type Empresa = ReturnType<typeof Empresa.parse>;
export type EmpresaResumo = ReturnType<typeof EmpresaResumo.parse>;
export type Notificacao = ReturnType<typeof Notificacao.parse>;
export type Interesse = (typeof INTERESSES)[number];
export type Porte = (typeof PORTES)[number];

export const FILTROS = {
  abertos: 'Abertos',
  interesse: 'Com resposta',
  todos: 'Todos',
} as const;
export type Filtro = keyof typeof FILTROS;

/** Mensagem do estado vazio por filtro. */
export const VAZIO: Record<Filtro, string> = {
  abertos: 'Nenhum edital aberto para você no momento. A equipe revisa os editais publicados todo dia útil.',
  interesse: 'Você ainda não respondeu a nenhum edital. Marque seu interesse nos editais abertos para a equipe acompanhar.',
  todos: 'Nenhum edital aprovado para você ainda. A equipe revisa os editais publicados todo dia útil.',
};

/** Ordem da lista, como a API aceita (`ordem=`); o padrão é o prazo mais próximo. */
export const ORDENS = {
  prazo: 'Prazo mais próximo',
  nota: 'Maior nota',
  publicacao: 'Mais recentes',
  valor: 'Maior valor',
} as const;
export type Ordem = keyof typeof ORDENS;

/** Atalhos de prazo (dias até o encerramento das propostas). */
export const PRAZOS = [3, 7, 15, 30] as const;

export type UF = (typeof UFS)[number];

/**
 * Filtros da lista como ficam na URL (`/?filtro=todos&etapa=preparando&q=saas&uf=SP`).
 * Só o que difere do padrão (`FILTROS_PADRAO`) é gravado; os opcionais ausentes não filtram.
 */
export interface FiltrosLista {
  filtro: Filtro;
  etapa?: Etapa;
  q?: string;
  uf?: UF;
  modalidade?: number;
  valorMinMinor?: number;
  valorMaxMinor?: number;
  prazoAteDias?: number;
  ordem: Ordem;
  /** `kanban` mostra o quadro por etapa; ausente = lista. Não vai para a API. */
  vista?: Vista;
}

export const VISTAS = { lista: 'Lista', kanban: 'Kanban' } as const;
export type Vista = keyof typeof VISTAS;

/** Kanban é a vista padrão do painel principal (decisão do Paulo, 23/10/2026); `vista=lista` troca. */
export const FILTROS_PADRAO = { filtro: 'abertos', ordem: 'prazo', vista: 'kanban' } as const satisfies FiltrosLista;

const inteiro = (v: unknown, min: number, max: number): number | undefined => {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() ? Number(v) : NaN;
  return Number.isInteger(n) && n >= min && n <= max ? n : undefined;
};

export const validarFiltro = (raw: Record<string, unknown>): FiltrosLista => ({
  filtro: typeof raw.filtro === 'string' && raw.filtro in FILTROS ? (raw.filtro as Filtro) : 'abertos',
  etapa: typeof raw.etapa === 'string' && (ETAPAS as readonly string[]).includes(raw.etapa) ? (raw.etapa as Etapa) : undefined,
  q: typeof raw.q === 'string' && raw.q.trim() ? raw.q.trim().slice(0, 200) : undefined,
  uf: typeof raw.uf === 'string' && (UFS_LISTA as readonly string[]).includes(raw.uf) ? (raw.uf as UF) : undefined,
  modalidade: inteiro(raw.modalidade, 1, 13),
  valorMinMinor: inteiro(raw.valorMinMinor, 0, Number.MAX_SAFE_INTEGER),
  valorMaxMinor: inteiro(raw.valorMaxMinor, 0, Number.MAX_SAFE_INTEGER),
  prazoAteDias: inteiro(raw.prazoAteDias, 1, 365),
  ordem: typeof raw.ordem === 'string' && raw.ordem in ORDENS ? (raw.ordem as Ordem) : 'prazo',
  vista: raw.vista === 'lista' ? 'lista' : 'kanban',
});

/**
 * Filtros que o quadro (kanban) manda à API: todas as etapas (uma coluna por etapa) e só "abertos" ou "todos"
 * (a aba "com resposta" não faz sentido por coluna).
 */
export const filtrosDoKanban = (f: FiltrosLista): FiltrosLista => ({
  ...f,
  etapa: undefined,
  filtro: f.filtro === 'abertos' ? 'abertos' : 'todos',
});

/** Algum filtro além da aba e da ordem padrão? (decide o botão "Limpar filtros" e o estado vazio). */
export const temFiltroAtivo = (f: FiltrosLista) =>
  f.etapa !== undefined ||
  f.q !== undefined ||
  f.uf !== undefined ||
  f.modalidade !== undefined ||
  f.valorMinMinor !== undefined ||
  f.valorMaxMinor !== undefined ||
  f.prazoAteDias !== undefined ||
  f.ordem !== 'prazo';

/** Query string para a API, só com o que está definido (a chave da consulta usa o mesmo objeto). */
function filtrosParaQuery(f: FiltrosLista): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(f)) if (v !== undefined && k !== 'vista') p.set(k, String(v));
  return p.toString();
}

// ---------- consultas ----------

export const empresaQuery = queryOptions({ queryKey: ['empresa'], queryFn: () => api<Empresa>('/app/empresa') });

/** Empresas pelas quais a pessoa pode agir (ordenadas pela API); a ativa vai no header `x-empresa`. */
export const empresasQuery = queryOptions({
  queryKey: ['empresas'],
  queryFn: () => api<{ itens: EmpresaResumo[] }>('/app/empresas'),
  staleTime: 5 * 60_000,
});

/** Últimas 50 notificações da empresa ativa; atualiza a cada minuto e ao voltar para a aba. */
export const notificacoesQuery = queryOptions({
  queryKey: ['notificacoes'],
  queryFn: () => api<{ naoLidas: number; itens: Notificacao[] }>('/app/notificacoes'),
  staleTime: 0,
  refetchInterval: 60_000,
  refetchOnWindowFocus: true,
});

/** Marca notificações como lidas (sem ids = todas). Atualiza o cache na hora e depois confere com a API. */
export async function marcarLidas(ids?: string[]): Promise<void> {
  const agora = new Date().toISOString();
  queryClient.setQueryData(notificacoesQuery.queryKey, (atual) => {
    if (!atual) return atual;
    const itens = atual.itens.map((n) => (n.lidaEm || (ids && !ids.includes(n.id)) ? n : { ...n, lidaEm: agora }));
    return { naoLidas: ids ? Math.max(0, atual.naoLidas - itens.filter((n, i) => n !== atual.itens[i]).length) : 0, itens };
  });
  try {
    await api<{ marcadas: number }>('/app/notificacoes/lidas', { method: 'POST', json: ids ? { ids } : {} });
  } finally {
    await queryClient.invalidateQueries({ queryKey: notificacoesQuery.queryKey });
  }
}

/** Lista de editais aprovados; a API filtra e ordena. Enquanto refiltra, mantém a lista anterior na tela. */
export const editaisQuery = (f: FiltrosLista) => {
  const qs = filtrosParaQuery(f);
  return queryOptions({
    queryKey: ['editais', qs],
    queryFn: () => api<{ itens: AppEdital[] }>(`/app/editais?${qs}`),
    placeholderData: (prev) => prev,
  });
};

/** Todos os editais aprovados, sem filtros: base do painel, dos números do topo, da paleta e do contador da barra lateral. */
export const editaisTodosQuery = editaisQuery({ filtro: 'todos', ordem: 'prazo' });

/** Um edital do painel: não há endpoint unitário, então vem da lista "todos" sem filtros (cache compartilhado). */
export const editalQuery = (fitId: string) =>
  queryOptions({
    ...editaisTodosQuery,
    placeholderData: undefined,
    select: (r) => r.itens.find((e) => e.fitId === fitId) ?? null,
  });

/** Salva o perfil (os contatos substituem a lista inteira) e atualiza o cache. */
export async function salvarEmpresa(body: EmpresaInput): Promise<Empresa> {
  const e = await api<Empresa>('/app/empresa', { method: 'PUT', json: body });
  queryClient.setQueryData(empresaQuery.queryKey, e);
  await queryClient.invalidateQueries({ queryKey: ['editais'] });
  return e;
}

/**
 * "Não me interessa" direto do cartão: o termo entra em `evitar` no perfil (minúsculas) e pesa só nos próximos fits.
 * Atualiza o cache da empresa com a lista devolvida e invalida a consulta (a página Empresa remonta o formulário).
 */
export async function evitarTermo(termo: string, acao: 'adicionar' | 'remover' = 'adicionar'): Promise<string[]> {
  const r = await api<{ evitar: string[] }>('/app/empresa/evitar', {
    method: acao === 'adicionar' ? 'POST' : 'DELETE',
    json: { termo: termo.trim().toLowerCase() },
  });
  queryClient.setQueryData(empresaQuery.queryKey, (atual) => (atual ? { ...atual, evitar: r.evitar } : atual));
  await queryClient.invalidateQueries({ queryKey: ['empresa'] });
  return r.evitar;
}

/** O termo já está na lista a evitar do perfil? (comparação sem caixa, como a API faz) */
export const jaEvitado = (evitar: readonly string[] | undefined, termo: string) => {
  const t = termo.trim().toLowerCase();
  return (evitar ?? []).some((x) => x.toLowerCase() === t);
};

/** Registra o interesse e invalida as listas (o item muda de aba). */
export async function responderInteresse(fitId: string, interesse: Interesse, observacao: string | null): Promise<void> {
  await api<{ ok: true }>(`/app/editais/${fitId}/interesse`, { method: 'POST', json: { interesse, observacao } });
  await queryClient.invalidateQueries({ queryKey: ['editais'] });
}

// ---------- rótulos e formatação ----------

export type Tone = 'ok' | 'warn' | 'bad' | 'info' | 'muted';

export const porteNome = (p: string) => PORTE_NOMES[p as Porte] ?? p;

/** Nota 0–100 → cor: ≥70 ok, 40–69 atenção, <40 ruim. */
export const notaTone = (nota: number | null | undefined): Tone =>
  nota == null ? 'muted' : nota >= 70 ? 'ok' : nota >= 40 ? 'warn' : 'bad';

/** Categoria da ficha (tipo principal da contratação) em português. */
export const CATEGORIAS: Record<string, string> = {
  software_licenca: 'Licença de software',
  software_desenvolvimento: 'Desenvolvimento de software',
  software_locacao_saas: 'Locação de software / SaaS',
  servico_ti: 'Serviço de TI',
  suporte_manutencao: 'Suporte e manutenção',
  dados_ia: 'Dados e IA',
  infraestrutura_nuvem: 'Infraestrutura e nuvem',
  consultoria: 'Consultoria',
  misto: 'Misto',
  fora_de_ti: 'Fora de TI',
};
export const categoriaNome = (c: string | null | undefined) => (c ? (CATEGORIAS[c] ?? c.replace(/_/g, ' ')) : null);

export const INTERESSE_TONE: Record<Interesse, Tone> = { participar: 'ok', nao: 'muted', ajuda: 'info' };

/** Centavos → "R$ 1.234" (sem centavos, para a lista). "—" se nulo. */
export const moneyInteiro = (minor: number | null | undefined) =>
  minor == null
    ? '—'
    : (minor / 100)
        .toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0, maximumFractionDigits: 0 })
        .replace(/\u00a0/g, ' ');

const fmtData = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeZone: 'America/Sao_Paulo' });
const fmtDataHora = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short', timeZone: 'America/Sao_Paulo' });
/** Instante ISO → "20/10/2026" no fuso de Brasília. */
export const dataBR = (iso: string | null | undefined) => (iso ? fmtData.format(new Date(iso)) : '—');
export const dataHoraBR = (iso: string | null | undefined) => (iso ? fmtDataHora.format(new Date(iso)) : '—');

/**
 * Prazo relativo ao agora: "encerrado", "hoje", "em 3 dias" (`curto` = "3 dias", para os chips).
 * Urgência: até 2 dias ruim, até 7 atenção, depois ok. `dias` é null sem prazo ou já encerrado.
 */
export function prazo(
  iso: string | null | undefined,
  agora = Date.now(),
): { texto: string; curto: string; tone: Tone; dias: number | null } {
  if (!iso) return { texto: 'sem prazo informado', curto: 'sem prazo', tone: 'muted', dias: null };
  const ms = new Date(iso).getTime() - agora;
  if (ms < 0) return { texto: 'encerrado', curto: 'encerrado', tone: 'muted', dias: null };
  const dias = Math.ceil(ms / 86_400_000);
  if (dias <= 0) return { texto: 'hoje', curto: 'hoje', tone: 'bad', dias: 0 };
  if (dias === 1) return { texto: 'em 1 dia', curto: '1 dia', tone: 'bad', dias };
  return { texto: `em ${dias} dias`, curto: `${dias} dias`, tone: dias <= 2 ? 'bad' : dias <= 7 ? 'warn' : 'ok', dias };
}

/** Centavos → "R$ 1,9 mi", "R$ 310 mil", "R$ 950" (para os números do topo e o rodapé dos cartões). */
export function moneyCompacto(minor: number | null | undefined): string {
  if (minor == null) return '—';
  const reais = minor / 100;
  const n = (v: number, dec: number) => v.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: dec });
  if (reais >= 1e9) return `R$ ${n(reais / 1e9, 1)} bi`;
  if (reais >= 1e6) return `R$ ${n(reais / 1e6, 1)} mi`;
  if (reais >= 1e3) return `R$ ${n(reais / 1e3, 0)} mil`;
  return `R$ ${n(reais, 0)}`;
}

/** "Javabipe Tecnologia" → "JT"; "ana" → "AN". */
export function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return '?';
  if (partes.length === 1) return partes[0]!.slice(0, 2).toUpperCase();
  return (partes[0]![0]! + partes[partes.length - 1]![0]!).toUpperCase();
}

const fmtDiaMes = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', timeZone: 'America/Sao_Paulo' });
const fmtHora = new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' });
/** Instante ISO → "27/10 09:00" (curto, para chips e cabeçalhos). */
export const diaHoraCurto = (iso: string) => `${fmtDiaMes.format(new Date(iso))} ${fmtHora.format(new Date(iso))}`;

/** Instante → "agora", "há 5 min", "há 3 h", "ontem", "há 4 dias" ou a data (mais de uma semana). */
export function tempoRelativo(iso: string, agora = Date.now()): string {
  const s = Math.max(0, Math.round((agora - new Date(iso).getTime()) / 1000));
  if (s < 60) return 'agora';
  const min = Math.floor(s / 60);
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h} h`;
  const dias = Math.floor(h / 24);
  if (dias === 1) return 'ontem';
  if (dias < 7) return `há ${dias} dias`;
  return dataBR(iso);
}

/** "12345678000195" → "12.345.678/0001-95" (devolve o original se não tiver 14 dígitos). */
export function cnpjFmt(v: string): string {
  const d = v.replace(/\D/g, '');
  return d.length === 14 ? `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}` : v;
}

/** "1.234,56" / "1234.56" / "R$ 1.234" → centavos; vazio → null; inválido → undefined. */
export function reaisParaMinor(texto: string): number | null | undefined {
  const t = texto.replace(/[R$\s]/g, '');
  if (!t) return null;
  // Vírgula é a casa decimal ("1.234,56"); sem vírgula, pontos de milhar ("1.200.000") ou decimal ("1234.56").
  const normal = t.includes(',') ? t.replace(/\./g, '').replace(',', '.') : /^\d{1,3}(\.\d{3})+$/.test(t) ? t.replace(/\./g, '') : t;
  if (!/^\d+(\.\d{0,2})?$/.test(normal)) return undefined;
  return Math.round(Number(normal) * 100);
}

/** Centavos → "1.234,56" (para preencher o campo). */
export const minorParaReais = (minor: number | null | undefined) =>
  minor == null ? '' : (minor / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
